import { z } from "zod"
import {
	isAwaitingStage,
	isTradeKind,
	TRADING_PRODUCTS,
	tradeDateToUtc,
} from "@/lib/trading"
import { paginatedSearch, stringList } from "./search"

const positiveNumberString = (label: string) =>
	z
		.string()
		.min(1, `${label} is required`)
		.refine((value) => Number(value) > 0, {
			message: "Must be greater than 0",
		})

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const MAX_QUANTITY = 10_000_000

function maxTradeDate(): Date {
	return new Date(Date.now() + 7 * ONE_DAY_MS)
}

function minTradeDate(): Date {
	const d = new Date()
	d.setFullYear(d.getFullYear() - 5)
	return d
}

const PRICE_FIELDS = {
	trade: ["buyPricePerUnit", "sellPricePerUnit"],
	broker: ["pricePerUnit", "sellerFeePerUnit", "buyerFeePerUnit"],
	buy: ["buyPricePerUnit"],
	sell: ["sellPricePerUnit"],
} as const

const sideSchema = z.enum(["company", "position", "none"])

type Side = z.infer<typeof sideSchema>

export function usesPosition(form: {
	source: Side
	destination: Side
}): boolean {
	return form.source === "position" || form.destination === "position"
}

const matchSchema = z.object({
	tradeId: z.string(),
	openQuantity: z.number(),
	quantity: z.number(),
	price: z.number(),
	flatFee: z.number(),
	companyName: z.string(),
})

export const tradeSchema = z
	.object({
		product: z.enum(TRADING_PRODUCTS),
		tradeType: z.enum(["trade", "broker"]),
		tradeDate: z.string().min(1, "Trade date is required"),
		source: sideSchema,
		destination: sideSchema,
		match: matchSchema.nullable(),
		supplierId: z.number().nullable(),
		supplierName: z.string(),
		buyerId: z.number().nullable(),
		buyerName: z.string(),
		quantity: positiveNumberString("Quantity")
			.refine((value) => Number.isInteger(Number(value)), {
				message: "Must be a whole number",
			})
			.refine((value) => Number(value) <= MAX_QUANTITY, {
				message: `Must be ${MAX_QUANTITY.toLocaleString()} or fewer`,
			}),
		paymentTermsDays: z.string(),
		buyPricePerUnit: z.string(),
		sellPricePerUnit: z.string(),
		flatFeeIn: z.string(),
		flatFeeOut: z.string(),
		pricePerUnit: z.string(),
		sellerFeePerUnit: z.string(),
		buyerFeePerUnit: z.string(),
		poolingFee: z.string(),
	})
	.superRefine((data, ctx) => {
		const issue = (path: string, message: string) =>
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
		const hasSupplier = data.source === "company"
		const hasBuyer = data.destination === "company"
		const linksPosition = usesPosition(data)

		if (!hasSupplier && !hasBuyer) {
			issue("destination", "One side must be a company")
		}
		if (hasSupplier && data.supplierId === null) {
			issue("supplierId", "Supplier is required")
		}
		if (hasBuyer && data.buyerId === null) {
			issue("buyerId", "Buyer is required")
		}
		if (
			hasSupplier &&
			hasBuyer &&
			data.supplierId !== null &&
			data.supplierId === data.buyerId
		) {
			issue("buyerId", "Supplier and buyer cannot be the same company")
		}
		if (linksPosition && !data.match) {
			issue("match", "Pick an open position")
		}
		if (
			linksPosition &&
			data.match &&
			Number(data.quantity) > data.match.openQuantity
		) {
			issue(
				"match",
				`Only ${data.match.openQuantity.toLocaleString("en-GB")} open`,
			)
		}
		if (hasBuyer) {
			const terms = Number(data.paymentTermsDays)
			if (!data.paymentTermsDays) {
				issue("paymentTermsDays", "Payment terms required")
			} else if (!(terms > 0 && terms <= 90)) {
				issue("paymentTermsDays", "Must be 1 to 90 days")
			}
		}

		if (data.tradeDate) {
			const tradeAt = tradeDateToUtc(data.tradeDate)
			if (!Number.isNaN(tradeAt.getTime())) {
				if (tradeAt > maxTradeDate()) {
					issue("tradeDate", "Cannot be more than 7 days in the future")
				} else if (tradeAt < minTradeDate()) {
					issue("tradeDate", "Cannot be more than 5 years in the past")
				}
			}
		}

		const priceKey =
			data.tradeType === "broker"
				? "broker"
				: !hasBuyer
					? "buy"
					: !hasSupplier
						? "sell"
						: "trade"
		for (const field of PRICE_FIELDS[priceKey]) {
			const value = data[field]
			if (!value) {
				issue(field, "Required")
			} else if (!(Number(value) > 0)) {
				issue(field, "Must be greater than 0")
			}
		}
	})
export type TradeInput = z.infer<typeof tradeSchema>

export const tradingSearchSchema = z.object({
	tab: z.enum(["trades", "quotes"]).optional().catch(undefined),
	tradeId: z.string().optional().catch(undefined),
	...paginatedSearch({ columnId: "tradeDate", direction: "desc" }),
	period: stringList,
	day: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional()
		.catch(undefined),
	product: stringList,
	kind: stringList.transform((values) => values.filter(isTradeKind)),
	linkedTo: z.string().optional().catch(undefined),
	status: stringList,
	awaiting: stringList.transform((values) => values.filter(isAwaitingStage)),
	supplierName: stringList,
	buyerName: stringList,
})

export type TradingSearch = z.output<typeof tradingSearchSchema>
